import React from "react";
import PageHero from "@/components/PageHero";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal } from "@/components/Reveal";

const services = [
  {
    n: "01",
    title: "Free In-Home Water Test",
    body: "A specialist visits your home, runs a full test on your water and shows you the results in plain English. No pressure. No obligation.",
  },
  {
    n: "02",
    title: "Water Softener Installation",
    body: "Non-electric, twin-tank Kinetico softeners sized precisely for your family's water use — installed by Vanguard, factory-trained by Kinetico.",
  },
  {
    n: "03",
    title: "Drinking Water & Reverse Osmosis",
    body: "K5, HYDRO ECO and AquaKinetic drinking-water stations. Bottle-quality water on tap for cooking, coffee, ice and every glass in between.",
  },
  {
    n: "04",
    title: "Whole-Home Filtration",
    body: "Chlorine, sediment, taste and odor filtered before it ever reaches your family's tap. Plus specialty solutions for sulfur, arsenic, iron and neutralizing acidic water.",
  },
  {
    n: "05",
    title: "Commercial Water Systems",
    body: "For restaurants, hotels, healthcare, salons and manufacturing across Northern Utah and Southern Idaho. Custom-sized systems built for uptime.",
  },
  {
    n: "06",
    title: "Softener Rental Options",
    body: "Not ready to buy? Rent a Kinetico softener short- or long-term with a service plan that includes salt delivery and maintenance.",
  },
  {
    n: "07",
    title: "Realtor Water Quality Reports",
    body: "Free water quality reports for real estate agents — help your buyers and sellers understand the water on the property before closing. Fast turnaround, professional documentation.",
  },
];

const specialty = [
  { name: "Sulfur Guard®", desc: "Eliminates hydrogen sulfide (rotten-egg smell) at the source." },
  { name: "Arsenic Guard®", desc: "Reduces naturally occurring arsenic — common in Western wells." },
  { name: "Dechlorinator", desc: "Whole-home chlorine removal for city water supplies." },
  { name: "Aerator", desc: "Removes iron and manganese that stain sinks, tubs and laundry." },
  { name: "Neutralizer", desc: "Balances acidic water that pinholes copper piping and damages fixtures." },
  { name: "PFAS-POU Filter", desc: "Targeted reduction of PFAS 'forever chemicals' at the tap." },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Services"
        title="Every drop of your water,"
        italic="handled."
        description="From water tests to softeners, drinking-water systems, whole-home filtration and commercial installs — one team, everything under one roof."
        testId="services-hero"
      />

      <section className="py-32 lg:py-40 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid grid-cols-12 gap-y-16 lg:gap-y-24 lg:gap-x-10">
            {services.map((s, i) => (
              <Reveal
                key={s.n}
                delay={i * 0.04}
                className={`col-span-12 lg:col-span-6 ${i % 2 === 1 ? "lg:mt-20" : ""}`}
                testId={`service-${s.n}`}
              >
                <div className="flex items-start gap-6 lg:gap-8">
                  <div className="font-display text-[clamp(3rem,5vw,4.5rem)] font-black text-[color:var(--green)] leading-none tabular shrink-0">
                    {s.n}
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-[clamp(1.5rem,2.2vw,2.2rem)] text-white leading-[1.05]">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-[15px] leading-[1.75] text-[color:var(--muted)] font-body max-w-lg">
                      {s.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 lg:py-40 px-6 lg:px-10 border-t border-[color:var(--border)]" data-testid="specialty-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-16">
            <div className="col-span-12 lg:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.35em] text-[color:var(--blue)]">
                Specialty Solutions
              </div>
              <Reveal>
                <h2 className="mt-4 font-display font-semibold text-[clamp(2rem,5vw,4rem)] leading-[0.95] text-white">
                  For the water<br />
                  <span className="font-editorial italic font-normal text-[color:var(--blue)]">
                    that fights back.
                  </span>
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-6 text-[15px] leading-[1.75] text-[color:var(--muted)] font-body max-w-md">
                  Not every home has the same water. When you've got sulfur smell, iron staining, arsenic, chlorine or acidic pH — we install the right specialty system to match.
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-10">
                {specialty.map((s, i) => (
                  <Reveal key={s.name} delay={i * 0.04} testId={`specialty-${i}`}>
                    <div className="pt-6 border-t border-[color:var(--border)]">
                      <h3 className="font-display font-semibold text-xl text-white">{s.name}</h3>
                      <p className="mt-3 text-[14px] leading-[1.7] text-[color:var(--muted)] font-body">
                        {s.desc}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <QuoteCTA />
    </div>
  );
}
